import type { GroupInviteeInput } from '../types/groups';

// Quote/newline-aware CSV tokenizer: a quoted field may contain commas AND
// newlines, so we walk the whole input rather than splitting on lines first.
export function parseCsvRecords(input: string): string[][] {
  const records: string[][] = [];
  let field = '';
  let row: string[] = [];
  let quoted = false;
  let pending = false; // any char/field seen for the current row

  const endField = () => {
    row.push(field.trim());
    field = '';
    pending = true;
  };
  const endRow = () => {
    endField();
    if (row.some((cell) => cell !== '')) records.push(row); // drop blank rows
    row = [];
    pending = false;
  };

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    const next = input[i + 1];
    if (quoted) {
      if (char === '"' && next === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
      pending = true;
    } else if (char === ',') {
      endField();
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && next === '\n') i += 1; // CRLF = one break
      endRow();
    } else {
      field += char;
      pending = true;
    }
  }
  if (pending || field !== '') endRow();
  return records;
}

export function parseInviteeImport(input: string): GroupInviteeInput[] {
  const rows = parseCsvRecords(input);

  if (!rows.length) return [];

  const first = rows[0].map((cell) => cell.toLowerCase().replace(/[\s_-]/g, ''));
  const hasHeader = first.some((cell) => ['name', 'fullname', 'email', 'phone', 'rsvp', 'rsvpstatus', 'status', 'notes', 'segment'].includes(cell));
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const headerIndex = (names: string[], fallback: number) => {
    if (!hasHeader) return fallback;
    const index = first.findIndex((cell) => names.includes(cell));
    return index >= 0 ? index : -1;
  };

  const nameIndex = headerIndex(['name', 'fullname'], 0);
  const emailIndex = headerIndex(['email'], 1);
  const phoneIndex = headerIndex(['phone', 'phonenumber', 'mobile'], 2);
  const statusIndex = headerIndex(['rsvp', 'rsvpstatus', 'status'], 3);
  const notesIndex = headerIndex(['notes', 'note'], 4);
  const segmentIndex = headerIndex(['segment'], 5);
  // Only send `segment` when the CSV actually carries one: a header that
  // declares the column (blank cells then clear the segment on upsert), or a
  // non-empty positional 6th column. Otherwise omit the key so re-importing a
  // segment-less CSV never wipes segments set elsewhere in Studio.
  const hasSegmentColumn = hasHeader && segmentIndex >= 0;

  // Whether the file DECLARES a column, not whether a given row filled it in.
  // rsvp_status and notes are preserve-unless-supplied on the wire, and the
  // signal for "supplied" has to be the file's shape: a blank RSVP cell under a
  // declared column is a host clearing it, a missing column is a host who never
  // mentioned RSVPs at all.
  //
  // Declared BY THE HEADER, not merely reachable by position. `headerIndex`
  // hands back its positional fallback whenever there is no header row, so a
  // bare `statusIndex >= 0` is true for every headerless paste — including the
  // two-column "Sarah,s@x.com" one — which put rsvpStatus:'unknown' and
  // notes:'' straight back on the wire for the exact paste this rule exists to
  // protect. A headerless row speaks for these columns only when it actually
  // carries a value in one, which is the rule `segment` already uses below.
  const headerDeclaresStatus = hasHeader && statusIndex >= 0;
  const headerDeclaresNotes = hasHeader && notesIndex >= 0;

  const invitees = dataRows.map((row) => {
    const fullName = nameIndex >= 0 ? row[nameIndex]?.trim() : '';
    const email = emailIndex >= 0 ? row[emailIndex]?.trim() : '';
    const phone = phoneIndex >= 0 ? row[phoneIndex]?.trim() : '';
    const rsvpStatus = statusIndex >= 0 ? row[statusIndex]?.trim() : '';
    const notes = notesIndex >= 0 ? row[notesIndex]?.trim() : '';
    const rawSegment = (segmentIndex >= 0 ? row[segmentIndex]?.trim() : '') || '';
    const invitee: GroupInviteeInput = {
      fullName,
      email: email || null,
      phone: phone || null,
      source: 'csv',
      // No synthetic export id. This used to mint `csv-${index}-${name}`, which
      // is an identity derived from a row's POSITION IN THE FILE: adding,
      // removing or reordering a single line re-keyed every row below it, so
      // re-pasting the same list matched nothing and inserted the whole roster
      // again. A row with no vendor id carries none, and the server matches on
      // the keys that mean something.
      sourceExternalId: null,
    };
    // Omitted keys, not empty ones: `undefined` is the wire's "preserve what is
    // stored". Defaulting these to 'unknown' and '' is what made pasting a
    // name+email CSV to attach addresses reset every matched guest's RSVP and
    // blank their notes.
    if (headerDeclaresStatus || rsvpStatus) invitee.rsvpStatus = rsvpStatus || 'unknown';
    if (headerDeclaresNotes || notes) invitee.notes = notes;
    if (hasSegmentColumn || rawSegment) {
      // Normalize "Travel Party" / "travel-party" -> 'travel_party'. A blank
      // cell under a declared segment column sends '' — the API's explicit
      // clear sentinel (null/absent means "preserve the stored segment").
      invitee.segment = rawSegment ? rawSegment.toLowerCase().replace(/[\s-]+/g, '_') : '';
    }
    return invitee;
  });

  const missingName = invitees.findIndex((invitee) => !invitee.fullName);
  if (missingName >= 0) {
    throw new Error(`Row ${missingName + (hasHeader ? 2 : 1)} is missing a name.`);
  }

  return invitees;
}
